import type { SupabaseClient } from "@supabase/supabase-js";

import type { AlertRow } from "./alert-queue";
import {
  DUTY_PING_COLUMNS,
  mapDutyPing,
  type DutyPing,
} from "./duty-pings";
import { capLiveTrails, LIVE_TRAIL_PAINT_CAP } from "./duty-live-presentation";
import {
  mapScanPoint,
  SCAN_POINT_COLUMNS,
  toDutyTrailFeatureCollection,
  type PointFeatureCollection,
  type ScanPoint,
} from "./map-points";
import { fetchTimeclockLocationPoints } from "./timeclock-location";
import { MAP_THEME } from "../lib/map-theme";

export const COMMAND_FOCUS_WINDOW_HOURS = 24;
export const COMMAND_FOCUS_POINT_LIMIT = 500;
export const COMMAND_FOCUS_GRADIENT_STEPS = 12;
export const COMMAND_FOCUS_MIN_TRAIL_OPACITY = 0.18;
export const COMMAND_FOCUS_MAX_TRAIL_OPACITY = 0.92;
export const COMMAND_FOCUS_SITE_QUERY_LIMIT =
  COMMAND_FOCUS_POINT_LIMIT * LIVE_TRAIL_PAINT_CAP;

export type CommandFocusEventKind = "ladder" | "scan" | "punch" | "sos";

export type CommandFocusSubject =
  | { kind: "officer"; routeAssignmentId: string; label?: string | undefined }
  | { kind: "call"; callId: string; callRef: string }
  | { kind: "site"; siteId: string; label: string };

export interface CommandFocusEventInput {
  id: string;
  kind: CommandFocusEventKind;
  label: string;
  occurredAt: string | null;
  lat: number | null;
  lng: number | null;
}

export interface CommandFocusEvent extends CommandFocusEventInput {
  lat: number;
  lng: number;
}

export interface ResolvedCommandFocusSubject {
  kind: CommandFocusSubject["kind"];
  label: string;
  routeAssignmentId: string | null;
  callId: string | null;
  siteId: string | null;
}

export type CommandFocusTrailCollection = GeoJSON.FeatureCollection<
  GeoJSON.LineString,
  {
    id: string;
    label: string;
    opacity: string;
    /** Identity colour for a site Replay tape. Absent on Focus, which draws one trail. */
    color?: string;
  }
>;

export interface CommandFocusSiteLabelSource {
  siteId: string;
  siteName: string | null;
}

export interface CommandFocusSiteOfficerInput {
  routeAssignmentId: string;
  label: string;
  /** An SOS inside this Focus window. */
  hasSos: boolean;
  /** The latest read or any sample inside the window is tied to a Call. */
  onCall: boolean;
  /** The bounce target names this officer, when that association is available. */
  subjectRelevant: boolean;
}

export interface CommandFocusSiteTrail {
  routeAssignmentId: string;
  label: string;
  pings: DutyPing[];
}

export interface CommandFocusSitePlan {
  drawn: CommandFocusSiteTrail[];
  drawnIds: string[];
  remainderIds: string[];
  remainderNote: string | null;
}

function asFiniteCoordinate(value: number | null, min: number, max: number): number | null {
  return value !== null && Number.isFinite(value) && value >= min && value <= max ? value : null;
}

function eventMillis(value: string | null): number {
  if (value === null || value.length === 0) return Number.NEGATIVE_INFINITY;
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : Number.NEGATIVE_INFINITY;
}

function pingMillis(ping: DutyPing): number {
  return eventMillis(ping.serverReceivedAt ?? ping.deviceTime);
}

/** Refuse incomplete or out-of-range event locations instead of inventing a tick. */
export function commandFocusEvent(input: CommandFocusEventInput): CommandFocusEvent | null {
  const lat = asFiniteCoordinate(input.lat, -90, 90);
  const lng = asFiniteCoordinate(input.lng, -180, 180);
  if (input.id.length === 0 || input.label.length === 0 || lat === null || lng === null) {
    return null;
  }
  return { ...input, lat, lng };
}

export function commandFocusBounceKind(
  source: "activity" | "call" | "sos" | "timeclock" | "unknown",
): CommandFocusEventKind | null {
  if (source === "activity") return "scan";
  if (source === "call") return "ladder";
  if (source === "sos") return "sos";
  if (source === "timeclock") return "punch";
  return null;
}

export function resolveCommandFocusSubject(
  subject: CommandFocusSubject,
  callPings: readonly DutyPing[] = [],
  officerLabel?: string | null,
): ResolvedCommandFocusSubject {
  if (subject.kind === "officer") {
    return {
      kind: "officer",
      label: subject.label ?? officerLabel ?? "Officer",
      routeAssignmentId: subject.routeAssignmentId,
      callId: null,
      siteId: null,
    };
  }
  if (subject.kind === "site") {
    return {
      kind: "site",
      label: subject.label,
      routeAssignmentId: null,
      callId: null,
      siteId: subject.siteId,
    };
  }
  const routeAssignmentId = callPings
    .slice()
    .sort((left, right) => pingMillis(right) - pingMillis(left))
    .find((ping) => ping.routeAssignmentId !== null && ping.routeAssignmentId.length > 0)
    ?.routeAssignmentId ?? null;
  return {
    kind: "call",
    label: `Call ${subject.callRef}`,
    routeAssignmentId,
    callId: subject.callId,
    siteId: null,
  };
}

export function commandFocusSubjectKey(subject: CommandFocusSubject | null): string {
  if (subject === null) return "live";
  if (subject.kind === "officer") return `officer:${subject.routeAssignmentId}`;
  if (subject.kind === "call") return `call:${subject.callId}`;
  return `site:${subject.siteId}`;
}

/**
 * Resolve site chrome without ever falling back to an id. Explicit human copy wins, then the
 * account site or boundary read, then the name-backed key used by a site cluster with no L0.
 */
export function commandFocusSiteLabel(
  subject: Extract<CommandFocusSubject, { kind: "site" }>,
  sources: readonly CommandFocusSiteLabelSource[],
): string {
  const explicit = subject.label.trim();
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (
    explicit.length > 0 &&
    explicit !== "Site" &&
    explicit !== subject.siteId &&
    !uuidLike.test(explicit)
  ) {
    return explicit;
  }
  const source = sources.find((row) => row.siteId === subject.siteId)?.siteName?.trim();
  if (source !== undefined && source.length > 0) {
    return source;
  }
  if (subject.siteId.startsWith("name:")) {
    const keyedName = subject.siteId.slice("name:".length).trim();
    if (keyedName.length > 0) return keyedName;
  }
  return "Site";
}

/** Keep only the newest 24 hours and 500 samples, then sort them chronologically. */
export function commandFocusTrailPings(
  pings: readonly DutyPing[],
  nowMs: number,
): DutyPing[] {
  const sinceMs = nowMs - COMMAND_FOCUS_WINDOW_HOURS * 3_600_000;
  return pings
    .filter((ping) => {
      const at = pingMillis(ping);
      return (
        ping.lat !== null &&
        ping.lng !== null &&
        Number.isFinite(ping.lat) &&
        Number.isFinite(ping.lng) &&
        ping.lat >= -90 &&
        ping.lat <= 90 &&
        ping.lng >= -180 &&
        ping.lng <= 180 &&
        at >= sinceMs &&
        at <= nowMs
      );
    })
    .sort((left, right) => pingMillis(left) - pingMillis(right))
    .slice(-COMMAND_FOCUS_POINT_LIMIT);
}

/**
 * One site story, ranked before the shared twelve-trail cap is applied.
 *
 * A bounce-linked officer or SOS stays visible first, then an on-Call officer, then the fuller
 * ping story. Ties use the newest sample and assignment id so polling cannot reshuffle tapes.
 */
export function planCommandFocusSiteTrails(
  officers: readonly CommandFocusSiteOfficerInput[],
  pings: readonly DutyPing[],
  nowMs: number,
): CommandFocusSitePlan {
  const officerById = new Map<string, CommandFocusSiteOfficerInput>();
  for (const officer of officers) {
    if (officer.routeAssignmentId.length > 0 && !officerById.has(officer.routeAssignmentId)) {
      officerById.set(officer.routeAssignmentId, officer);
    }
  }
  const pingsById = new Map<string, DutyPing[]>();
  for (const ping of pings) {
    const id = ping.routeAssignmentId;
    if (id === null || !officerById.has(id)) continue;
    const bucket = pingsById.get(id);
    if (bucket === undefined) pingsById.set(id, [ping]);
    else bucket.push(ping);
  }

  const candidates = [...officerById.values()].flatMap((officer) => {
    const story = commandFocusTrailPings(pingsById.get(officer.routeAssignmentId) ?? [], nowMs);
    if (story.length === 0) return [];
    return [{
      officer,
      story,
      onCall: officer.onCall || story.some((ping) => ping.dutyCallId !== null),
      newestMs: pingMillis(story[story.length - 1]!),
    }];
  });
  candidates.sort((left, right) => {
    const leftSafety = left.officer.subjectRelevant || left.officer.hasSos ? 0 : 1;
    const rightSafety = right.officer.subjectRelevant || right.officer.hasSos ? 0 : 1;
    if (leftSafety !== rightSafety) return leftSafety - rightSafety;
    if (left.onCall !== right.onCall) return left.onCall ? -1 : 1;
    if (left.story.length !== right.story.length) return right.story.length - left.story.length;
    if (left.newestMs !== right.newestMs) return right.newestMs - left.newestMs;
    return left.officer.routeAssignmentId.localeCompare(right.officer.routeAssignmentId);
  });

  const capped = capLiveTrails(
    candidates.map((candidate, rank) => ({
      id: candidate.officer.routeAssignmentId,
      rank,
    })),
  );
  const candidateById = new Map(
    candidates.map((candidate) => [candidate.officer.routeAssignmentId, candidate] as const),
  );
  const drawn = capped.drawnIds.flatMap((id) => {
    const candidate = candidateById.get(id);
    return candidate === undefined
      ? []
      : [{
          routeAssignmentId: id,
          label: candidate.officer.label,
          pings: candidate.story,
        }];
  });
  return {
    drawn,
    drawnIds: capped.drawnIds,
    remainderIds: capped.remainderIds,
    remainderNote:
      capped.remainderIds.length === 0
        ? null
        : `+${capped.remainderIds.length} officer${capped.remainderIds.length === 1 ? "" : "s"} not drawn.`,
  };
}

/**
 * Build a small set of chronological line segments over an explicit window. Older segments are
 * faint and every newer segment is at least as strong as the segment before it.
 *
 * The window is a parameter rather than a fixed 24 hours because Replay scrubs an arbitrary day
 * or Call and needs the same gradient read against ITS window, with the strongest segment at the
 * playhead. One gradient implementation, two desks: forking it would let Focus and Replay drift
 * into two different meanings for the same fading tape.
 */
export function toGradientTrailFeatureCollection(
  samples: readonly DutyPing[],
  trailId: string,
  label: string,
  window: { startMs: number; endMs: number },
): CommandFocusTrailCollection {
  const routeAssignmentId = trailId;
  const baseTrail = toDutyTrailFeatureCollection(samples, routeAssignmentId, label);
  const coordinates = baseTrail.features[0]?.geometry.coordinates ?? [];
  if (coordinates.length < 2) return { type: "FeatureCollection", features: [] };

  const nowMs = window.endMs;
  const windowMs = Math.max(1, window.endMs - window.startMs);
  const features: CommandFocusTrailCollection["features"] = [];
  let activeStep = -1;

  for (let index = 1; index < samples.length; index += 1) {
    const previous = samples[index - 1];
    const current = samples[index];
    if (
      previous?.lat === null ||
      previous?.lng === null ||
      current?.lat === null ||
      current?.lng === null ||
      previous === undefined ||
      current === undefined
    ) {
      continue;
    }
    const ageRatio = Math.min(1, Math.max(0, (nowMs - pingMillis(current)) / windowMs));
    const freshness = 1 - ageRatio;
    const step = Math.min(
      COMMAND_FOCUS_GRADIENT_STEPS - 1,
      Math.floor(freshness * COMMAND_FOCUS_GRADIENT_STEPS),
    );
    const opacity =
      COMMAND_FOCUS_MIN_TRAIL_OPACITY +
      (step / (COMMAND_FOCUS_GRADIENT_STEPS - 1)) *
        (COMMAND_FOCUS_MAX_TRAIL_OPACITY - COMMAND_FOCUS_MIN_TRAIL_OPACITY);
    const previousPosition = coordinates[index - 1];
    const currentPosition = coordinates[index];
    if (previousPosition === undefined || currentPosition === undefined) continue;
    const previousCoord: [number, number] = [previousPosition[0]!, previousPosition[1]!];
    const currentCoord: [number, number] = [currentPosition[0]!, currentPosition[1]!];
    const last = features[features.length - 1];
    if (last !== undefined && activeStep === step) {
      last.geometry.coordinates.push(currentCoord);
      continue;
    }
    activeStep = step;
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [previousCoord, currentCoord] },
      properties: {
        id: `${routeAssignmentId}:${step}:${index}`,
        label,
        opacity: opacity.toFixed(3),
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Focus keeps its fixed 24 hour window; the gradient itself is the shared helper above. */
export function toCommandFocusTrailFeatureCollection(
  pings: readonly DutyPing[],
  routeAssignmentId: string,
  label: string,
  nowMs: number,
): CommandFocusTrailCollection {
  return toGradientTrailFeatureCollection(
    commandFocusTrailPings(pings, nowMs),
    routeAssignmentId,
    label,
    { startMs: nowMs - COMMAND_FOCUS_WINDOW_HOURS * 3_600_000, endMs: nowMs },
  );
}

/**
 * Build the existing age-gradient trail for every drawn site officer. The shared identity
 * palette has twelve safe colours and excludes red and SOS coral.
 */
export function toCommandFocusSiteTrailFeatureCollection(
  plan: CommandFocusSitePlan,
  nowMs: number,
): CommandFocusTrailCollection {
  const features: CommandFocusTrailCollection["features"] = [];
  plan.drawn.forEach((trail, index) => {
    const color =
      MAP_THEME.replayIdentityTrail[index % MAP_THEME.replayIdentityTrail.length]
      ?? MAP_THEME.dutyTrailLineFocus;
    const collection = toCommandFocusTrailFeatureCollection(
      trail.pings,
      trail.routeAssignmentId,
      `${trail.label} trail`,
      nowMs,
    );
    for (const feature of collection.features) {
      features.push({
        ...feature,
        properties: { ...feature.properties, color },
      });
    }
  });
  return { type: "FeatureCollection", features };
}

/**
 * One bounded read for the roster assignments in the active site story. Rows are still checked
 * against the requested ids after RLS returns them, and each trail is capped again by the pure
 * planner before paint.
 */
export async function fetchCommandFocusSitePings(
  client: SupabaseClient,
  accountId: string,
  routeAssignmentIds: readonly string[],
  sinceIso: string,
): Promise<DutyPing[]> {
  const ids = [...new Set(routeAssignmentIds.filter((id) => id.length > 0))];
  if (ids.length === 0) return [];
  const allowed = new Set(ids);
  const { data, error } = await client
    .from("duty_location_pings")
    .select(DUTY_PING_COLUMNS)
    .eq("account_id", accountId)
    .in("route_assignment_id", ids)
    .gte("server_received_at", sinceIso)
    .order("server_received_at", { ascending: false })
    .limit(COMMAND_FOCUS_SITE_QUERY_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load the site Focus story.");
  }
  return (data as Array<Record<string, unknown>>)
    .map((row) => mapDutyPing(row))
    .filter((ping) => ping.routeAssignmentId !== null && allowed.has(ping.routeAssignmentId));
}

const FOCUS_TICK_TOKENS: Record<CommandFocusEventKind, string> = {
  ladder: "L",
  scan: "NFC",
  punch: "T",
  sos: "SOS",
};

export function toCommandFocusTickFeatureCollection(
  events: readonly CommandFocusEventInput[],
  selectedEventId: string | null,
): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: events.flatMap((input) => {
      const event = commandFocusEvent(input);
      if (event === null) return [];
      return [
        {
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [event.lng, event.lat] as [number, number] },
          properties: {
            id: event.id,
            name: event.label,
            label: event.label,
            kind: event.kind,
            token: FOCUS_TICK_TOKENS[event.kind],
            selected: event.id === selectedEventId ? "true" : "false",
            status: event.occurredAt === null ? "Time not reported" : "Recorded event",
            layerLabel: "Focus event",
          },
        },
      ];
    }),
  };
}

export function focusScanEvents(
  scans: readonly ScanPoint[],
  resolved: ResolvedCommandFocusSubject,
  sinceIso: string,
  routeAssignmentIds: readonly string[] = [],
): CommandFocusEvent[] {
  const sinceMs = eventMillis(sinceIso);
  const siteAssignments = new Set(routeAssignmentIds);
  return scans.flatMap((scan) => {
    const scanAssignmentId = scan.routeAssignmentId ?? null;
    const belongs =
      resolved.routeAssignmentId !== null
        ? scanAssignmentId === resolved.routeAssignmentId
        : resolved.siteId !== null &&
          (siteAssignments.size > 0
            ? scanAssignmentId !== null && siteAssignments.has(scanAssignmentId)
            : scan.siteId === resolved.siteId);
    if (
      !belongs ||
      scan.mapCoordSource !== "scan_gps" ||
      eventMillis(scan.serverReceivedAt ?? null) < sinceMs
    ) {
      return [];
    }
    const event = commandFocusEvent({
      id: `scan:${scan.scanPointId}`,
      kind: "scan",
      label: scan.checkpointName ?? "Check-in",
      occurredAt: scan.serverReceivedAt ?? null,
      lat: scan.lat,
      lng: scan.lng,
    });
    return event === null ? [] : [event];
  });
}

/** Account and subject scoped check-in points for the active 24 hour Focus story. */
export async function fetchFocusScanPoints(
  client: SupabaseClient,
  accountId: string,
  resolved: ResolvedCommandFocusSubject,
  sinceIso: string,
  routeAssignmentIds: readonly string[] = [],
): Promise<ScanPoint[]> {
  const ids = resolved.routeAssignmentId === null
    ? [...new Set(routeAssignmentIds.filter((id) => id.length > 0))]
    : [resolved.routeAssignmentId];
  if (ids.length === 0) return [];
  let query = client
    .from("supervisor_scan_points")
    .select(SCAN_POINT_COLUMNS)
    .eq("account_id", accountId);
  query = ids.length === 1
    ? query.eq("route_assignment_id", ids[0]!)
    : query.in("route_assignment_id", ids);
  const { data, error } = await query
    .gte("server_received_at", sinceIso)
    .order("server_received_at", { ascending: false })
    .limit(COMMAND_FOCUS_POINT_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load Focus check-ins.");
  }
  return (data as Array<Record<string, unknown>>)
    .map((row) => mapScanPoint(row))
    .filter((point): point is ScanPoint => point !== null);
}

export function focusSosEvents(
  alerts: readonly AlertRow[],
  resolved: ResolvedCommandFocusSubject,
  sinceIso: string,
): CommandFocusEvent[] {
  const sinceMs = eventMillis(sinceIso);
  return alerts.flatMap((alert) => {
    const belongs =
      alert.alertType === "sos" &&
      (resolved.routeAssignmentId !== null
        ? alert.routeAssignmentId === resolved.routeAssignmentId
        : resolved.siteId !== null && alert.siteId === resolved.siteId);
    if (!belongs || eventMillis(alert.detectedAt) < sinceMs) return [];
    const event = commandFocusEvent({
      id: `sos:${alert.alertEventId}`,
      kind: "sos",
      label: `SOS${alert.guardLabel === null ? "" : ` · ${alert.guardLabel}`}`,
      occurredAt: alert.detectedAt,
      lat: alert.dutyLat,
      lng: alert.dutyLng,
    });
    return event === null ? [] : [event];
  });
}

export function mergeCommandFocusEvents(
  groups: readonly (readonly CommandFocusEventInput[])[],
): CommandFocusEvent[] {
  const byId = new Map<string, CommandFocusEvent>();
  for (const group of groups) {
    for (const input of group) {
      const event = commandFocusEvent(input);
      if (event !== null) byId.set(event.id, event);
    }
  }
  return [...byId.values()].sort((left, right) => eventMillis(left.occurredAt) - eventMillis(right.occurredAt));
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** One explicit Call rung coordinate read behind a Show on map action. */
export async function fetchDutyCallEventPoint(
  client: SupabaseClient,
  accountId: string,
  callId: string,
  eventSeq: number,
): Promise<CommandFocusEvent | null> {
  const { data, error } = await client
    .from("duty_call_status_events")
    .select("duty_call_id,event_type,recorded_at,event_seq,lat,lng,gps_captured_at")
    .eq("account_id", accountId)
    .eq("duty_call_id", callId)
    .eq("event_seq", eventSeq)
    .limit(1);
  if (error !== null || !Array.isArray(data)) return null;
  const row = data[0];
  if (row === null || typeof row !== "object") return null;
  const raw = row as Record<string, unknown>;
  const returnedCallId = asString(raw.duty_call_id);
  const returnedEventSeq = asNumber(raw.event_seq);
  const type = asString(raw.event_type);
  if (
    returnedCallId !== callId ||
    returnedEventSeq !== eventSeq ||
    asString(raw.gps_captured_at) === null
  ) return null;
  return commandFocusEvent({
    id: `call:${callId}:${eventSeq}`,
    kind: "ladder",
    label: type === null ? "Call event" : type.replaceAll("_", " "),
    occurredAt: asString(raw.recorded_at),
    lat: asNumber(raw.lat),
    lng: asNumber(raw.lng),
  });
}

export async function fetchFocusLadderEvents(
  client: SupabaseClient,
  accountId: string,
  callIds: readonly string[],
  sinceIso: string,
): Promise<CommandFocusEvent[]> {
  if (callIds.length === 0) return [];
  const allowedCallIds = new Set(callIds);
  const { data, error } = await client
    .from("duty_call_status_events")
    .select("duty_call_id,event_type,recorded_at,event_seq,lat,lng,gps_captured_at")
    .eq("account_id", accountId)
    .in("duty_call_id", [...callIds])
    .gte("recorded_at", sinceIso)
    .order("recorded_at", { ascending: false })
    .limit(200);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load Focus Call events.");
  }
  return data.flatMap((row) => {
    if (row === null || typeof row !== "object") return [];
    const raw = row as Record<string, unknown>;
    const callId = asString(raw.duty_call_id);
    const type = asString(raw.event_type);
    const eventSeq = asNumber(raw.event_seq);
    if (
      callId === null ||
      !allowedCallIds.has(callId) ||
      type === null ||
      eventSeq === null ||
      asString(raw.gps_captured_at) === null
    ) return [];
    const event = commandFocusEvent({
      id: `call:${callId}:${eventSeq}`,
      kind: "ladder",
      label: type.replaceAll("_", " "),
      occurredAt: asString(raw.recorded_at),
      lat: asNumber(raw.lat),
      lng: asNumber(raw.lng),
    });
    return event === null ? [] : [event];
  });
}

export async function fetchFocusPunchEvents(
  client: SupabaseClient,
  accountId: string,
  routeAssignmentId: string | readonly string[] | null,
  sinceIso: string,
): Promise<CommandFocusEvent[]> {
  const ids = routeAssignmentId === null
    ? []
    : [...new Set(
        (typeof routeAssignmentId === "string" ? [routeAssignmentId] : routeAssignmentId)
          .filter((id) => id.length > 0),
      )];
  if (ids.length === 0) return [];
  let query = client
    .from("management_timeclock_log")
    .select("time_punch_id,route_assignment_id,punch_type,server_received_at,device_time,guard_label")
    .eq("account_id", accountId);
  query = ids.length === 1
    ? query.eq("route_assignment_id", ids[0]!)
    : query.in("route_assignment_id", ids);
  const log = await query
    .gte("server_received_at", sinceIso)
    .order("server_received_at", { ascending: false })
    .limit(200);
  if (log.error !== null || !Array.isArray(log.data)) {
    throw new Error("Could not load Focus time punches.");
  }
  const allowedAssignments = new Set(ids);
  const rows = (log.data as unknown[]).filter(
    (row): row is Record<string, unknown> =>
      row !== null &&
      typeof row === "object" &&
      allowedAssignments.has(asString((row as Record<string, unknown>).route_assignment_id) ?? ""),
  );
  const punchIds = rows
    .map((row) => asString(row.time_punch_id))
    .filter((id): id is string => id !== null);
  if (punchIds.length === 0) return [];
  const points = await fetchTimeclockLocationPoints(client, accountId, punchIds);
  if (points.status !== "found") {
    throw new Error("Could not load Focus time punch locations.");
  }
  const pointsById = new Map(points.points.map((point) => [point.timePunchId, point]));
  return rows.flatMap((row) => {
    const id = asString(row.time_punch_id);
    if (id === null) return [];
    const point = pointsById.get(id);
    if (point === undefined) return [];
    const type = asString(row.punch_type) ?? "Time punch";
    const guard = asString(row.guard_label);
    const event = commandFocusEvent({
      id: `punch:${id}`,
      kind: "punch",
      label: `${type.replaceAll("_", " ")}${guard === null ? "" : ` · ${guard}`}`,
      occurredAt: asString(row.server_received_at) ?? asString(row.device_time),
      lat: asNumber(point.lat),
      lng: asNumber(point.lng),
    });
    return event === null ? [] : [event];
  });
}
